import {
  useAssignAiConfigurationToProjectsMutation,
  useCreateAiConfigurationMutation,
} from '@lokalise/shared'
import type { AiConfig, CreateAiConfigsRequestBody } from '@lokalise/translation-api-schemas'
import { RagAnalyticsEventName } from '@lokalise/types'
import { useRagAnalytics } from '../providers/analytics'
import {
  appHttpClient,
  authenticatedPublicApiHeaderBuilder,
  getTeamIdFromSession,
} from '../utils/api'

type UseCreateProfileProps = {
  onCreateSuccess: (profile: AiConfig) => void
  onSuccess: (profile: AiConfig) => void
  onError: (error: unknown) => void
}

type CreateProps = {
  projects: string[]
  profile: CreateAiConfigsRequestBody
}

export const useCreateProfile = ({
  onSuccess,
  onCreateSuccess,
  onError,
}: UseCreateProfileProps) => {
  const teamId = getTeamIdFromSession()

  const { mutateAsync: createAiConfig, isPending: isCreatingAiConfig } =
    useCreateAiConfigurationMutation(appHttpClient, authenticatedPublicApiHeaderBuilder, {
      teamId,
    })

  const { mutateAsync: assignAiConfig, isPending: isAssigningAiConfig } =
    useAssignAiConfigurationToProjectsMutation(appHttpClient, authenticatedPublicApiHeaderBuilder)

  const track = useRagAnalytics()

  const create = async ({ projects, profile }: CreateProps) => {
    try {
      const createdProfile = await createAiConfig(profile)

      onCreateSuccess(createdProfile.data)
      track(RagAnalyticsEventName.AI_PROFILE_CREATED, { profile_id: createdProfile.data.id })

      await assignAiConfig({
        project_ids: projects,
        teamId,
        configurationId: createdProfile.data.id,
      })

      onSuccess(createdProfile.data)
    } catch (error) {
      onError(error)
    }
  }

  return { create, isPending: isCreatingAiConfig || isAssigningAiConfig }
}
